import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import type { GridSize } from "@mui/material/Grid";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Portal from "@mui/material/Portal";
import Typography from "@mui/material/Typography";
import Icon, { ResizeBottomRightIcon } from "common/components/Elements/Icon/Icon";
import { generateRandomString } from "common/utilities/core/string";
import useWindowResize from "common/utilities/hooks/useWindowResize/useWindowResize";
import { resetCloseModalAction } from "core/redux/actions/ui/uiActions";
import type { ResetCloseModal } from "core/redux/actions/ui/uiActions.types";
import type { ApplicationState } from "core/redux/reducers/rootReducer";
import type { AppDispatch } from "core/redux/store/configureStore.types";
import theme, { sx } from "core/utilities/theme";
import { SCROLLBAR_WIDTH } from "dataAnalyzer/components/Chart/useScrollableChart";
import I18n from "i18n/en-US";
import type { JSX, RefObject } from "react";
import React, { useRef } from "react";
import type { ControlPosition } from "react-draggable";
import Draggable from "react-draggable";
import { connect } from "react-redux";
import type { ResizeCallbackData } from "react-resizable";
import { ResizableBox } from "react-resizable";

import classes from "./SimpleModal.style";

const modalHeaderID = "modalHeader";

function setModalHeaderWidth(containerEl: HTMLElement, modalWidth: number) {
  const headerEl = containerEl.querySelector(`#${modalHeaderID}`);
  if (headerEl) {
    (headerEl as HTMLElement).style.maxWidth = `${modalWidth - 16}px`;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any, react/display-name
const ResizableHandle = React.forwardRef((props: any, ref) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { handleAxis, noActions, ...restProps } = props;

  return (
    <Box
      ref={ref}
      sx={{
        position: "absolute",
        // position handle at the bottom right of the modal
        bottom: noActions ? "-22px" : "-84px",
        right: "-15px",
        cursor: "nwse-resize",
      }}
      {...restProps}
    >
      <ResizeBottomRightIcon />
    </Box>
  );
});

interface CalculateSizeWrapperProps {
  calculateSize: (ref: HTMLDivElement) => void;
  children: React.ReactNode;
}

function CalculateSizeWrapper({ calculateSize, children }: CalculateSizeWrapperProps) {
  const wrapperRef = useRef<HTMLDivElement>(undefined);

  useWindowResize(() => {
    if (wrapperRef.current) calculateSize(wrapperRef.current);
  });

  return (
    <div
      ref={(ref) => {
        if (ref) {
          wrapperRef.current = ref;
          calculateSize(ref);
        }
      }}
    >
      {children}
    </div>
  );
}

interface ScrollableModalContentProps {
  children: JSX.Element;
  resizable?: ResizeCallbackData["size"];
  onResize?: (size: ResizeCallbackData["size"]) => void;
  noActions?: boolean;
}

export function ScrollableModalContent({ children, resizable, onResize, noActions }: ScrollableModalContentProps) {
  const ScrollableContent = (
    <Box
      id="scrollableContent"
      sx={{
        overflowX: "auto",
        overflowY: "auto",
        // Must add a padding otherwise the scrollbar is always shown when using max-height.
        pb: 1,
        pr: 1,
      }}
    >
      {children}
    </Box>
  );

  if (!resizable) return ScrollableContent;

  return (
    <ResizableBox
      width={resizable.width}
      height={resizable.height}
      resizeHandles={["se"]}
      onResize={(_e, data) => {
        if (onResize) onResize(data.size);
      }}
      handle={<ResizableHandle noActions={noActions} />}
      style={{
        position: "relative",
      }}
      minConstraints={[400, 400]}
    >
      {ScrollableContent}
    </ResizableBox>
  );
}

interface PropsFromState {
  closeModal?: string;
}

interface OwnProps {
  title: string;
  // Specify either:
  // - Trigger open: add a trigger element, usually a button
  // - Manual open: set the 'open' attribute to open/close the modal
  trigger?: (onClick: () => void) => JSX.Element;
  open?: boolean;
  content: JSX.Element;
  dispatchResetCloseModalAction: ResetCloseModal;
  size?: "default" | "auto" | "large" | "full";
  /** Given modal width  */
  width?: number;
  onClose?: () => void;
  draggable?: boolean;
  disableClose?: boolean;
  /**
   * Create a draggable poppover where the user can still interact with the content behind.
   * Width of the modal must be proved via the width attribute.
   */
  draggablePoppover?: boolean;
  defaultPosition?: ControlPosition;
  headerActions?: JSX.Element;
}

export type ComponentProps = PropsFromState & OwnProps;

interface ComponentState {
  id: string;
  open: boolean;
  childRef?: RefObject<HTMLElement | null>;
}

class SimpleModal extends React.Component<ComponentProps, ComponentState> {
  constructor(props: ComponentProps) {
    super(props);

    this.state = {
      id: generateRandomString(),
      open: props.open || false,
      childRef: React.createRef<HTMLElement | null>(),
    };
  }

  componentDidUpdate() {
    const { closeModal, dispatchResetCloseModalAction, open: forceOpen } = this.props;
    const { id, open: stateOpen } = this.state;
    const open = forceOpen === undefined ? stateOpen : forceOpen;

    if (open && (closeModal === id || closeModal === "all")) {
      this.handleClose();
      dispatchResetCloseModalAction();
    }
  }

  handleOpen = () => {
    this.setState({ open: true });
  };

  handleClose = () => {
    const { onClose } = this.props;
    const { open } = this.state;

    if (open) {
      this.setState({ open: false });
      if (onClose) onClose();
    }
  };

  handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (event.target && (event.target as HTMLElement).classList.contains("MuiGrid-root")) {
      this.handleClose();
    }
  };

  calculateSize = (ref: HTMLDivElement) => {
    const { size } = this.props;
    this.calculateHeight(ref);

    if (size !== "full") this.calculateWidth(ref);
  };

  calculateHeight = (ref: HTMLDivElement, retry?: number) => {
    const scrollableContentEl = ref.querySelector("#scrollableContent") as HTMLDivElement;
    if (scrollableContentEl) {
      const modalContentHeight = (ref.parentElement as HTMLDivElement).offsetHeight;
      const windowHeight = window.innerHeight;
      const modalContentUnscrollable = modalContentHeight - scrollableContentEl.offsetHeight;
      const margin = 32;
      let maxHeight = windowHeight - margin * 2 - modalContentUnscrollable;

      // Resizable content sets the max height instead of available space in the viewport
      const resizableContentEl = ref.querySelector(".react-resizable") as HTMLDivElement;
      if (resizableContentEl) {
        maxHeight = resizableContentEl.offsetHeight + SCROLLBAR_WIDTH;
      }

      scrollableContentEl.style.maxHeight = `${maxHeight}px`;
    } else {
      // Some content might not be rendered right away, so we wait until we get the element
      if (retry && retry > 5) return;
      setTimeout(() => {
        this.calculateHeight(ref, retry ? retry + 1 : 0);
      }, 500);
    }
  };

  calculateWidth = (ref: HTMLDivElement, retry?: number) => {
    const scrollableContentEl = ref.querySelector("#scrollableContent") as HTMLDivElement;
    if (scrollableContentEl) {
      const modalContentWidth = (ref.parentElement as HTMLDivElement).offsetWidth;
      const windowWidth = window.innerWidth;
      const modalContentUnscrollable = modalContentWidth - scrollableContentEl.offsetWidth;
      const margin = 32;
      let maxWidth = windowWidth - margin * 2 - modalContentUnscrollable;

      // Resizable content sets the max Width instead of available space in the viewport
      const resizableContentEl = ref.querySelector(".react-resizable") as HTMLDivElement;
      if (resizableContentEl) {
        maxWidth = resizableContentEl.offsetWidth + SCROLLBAR_WIDTH;
      }

      scrollableContentEl.style.maxWidth = `${maxWidth}px`;
      setModalHeaderWidth(ref, maxWidth);
    } else {
      // Some content might not be rendered right away, so we wait until we get the element
      if (retry && retry > 5) return;
      setTimeout(() => {
        this.calculateWidth(ref, retry ? retry + 1 : 0);
      }, 500);
    }
  };

  render() {
    const {
      title,
      trigger,
      content,
      size,
      open: forceOpen,
      draggable,
      disableClose,
      draggablePoppover,
      width,
      defaultPosition,
      headerActions,
    } = this.props;
    const { id, open: stateOpen } = this.state;
    const open = forceOpen === undefined ? stateOpen : forceOpen;

    let xs: GridSize = 12;
    let sm: GridSize = 10;
    let md: GridSize = 8;
    let lg: GridSize = 6;

    if (size === "auto") {
      xs = "auto";
      sm = "auto";
      md = "auto";
      lg = "auto";
    } else if (size === "large") {
      xs = 12;
      sm = 12;
      md = 10;
      lg = 8;
    } else if (size === "full") {
      xs = 12;
      sm = xs;
      md = xs;
      lg = xs;
    }

    const draggableElementId = "draggable-modal-title";

    const DraggableContent = (
      <Draggable
        disabled={!draggable && !draggablePoppover}
        handle={`#${draggableElementId}`}
        cancel={'[class*="MuiDialogContent-root"]'}
        defaultPosition={defaultPosition}
        nodeRef={this.state.childRef}
      >
        <Paper
          id="modalContent"
          // Used within ModalActions to target the parent SimpleModal
          data-modal-id={id}
          sx={sx(
            draggablePoppover ? classes.paperDraggablePoppover : classes.paper,
            width === undefined ? {} : { width: `${width}px` },
          )}
          ref={this.state.childRef as RefObject<HTMLDivElement>}
        >
          <CalculateSizeWrapper calculateSize={this.calculateSize}>
            <Box
              id={modalHeaderID}
              sx={{
                display: "flex",
                alignItems: "start",
                mt: theme.spacing(-0.5),
                cursor: draggable || draggablePoppover ? "move" : undefined,
              }}
            >
              <Box
                id={draggableElementId}
                sx={sx({ display: "flex", flexGrow: 1 }, draggable ? { cursor: "move", ml: -0.5 } : undefined)}
              >
                <Box sx={{ display: "inline-block", flexGrow: 1 }}>
                  {(draggable || draggablePoppover) && (
                    <Icon element={DragIndicatorIcon} mr style={{ marginTop: "6px" }} />
                  )}
                  <Typography component="h2" variant="h5" sx={{ display: "inline-block", flexGrow: 1 }}>
                    {title}
                  </Typography>
                </Box>
                {headerActions && <Box sx={{ display: "flex", mt: 0.25, mr: 1 }}>{headerActions}</Box>}
              </Box>
              {!disableClose && (
                <IconButton
                  aria-label={I18n.Elements.Modal.SimpleModal.close}
                  onClick={this.handleClose}
                  data-e2e-modal="close"
                  size="small"
                  sx={{ mr: -1, mt: "2px" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            {content}
          </CalculateSizeWrapper>
        </Paper>
      </Draggable>
    );

    if (draggablePoppover) {
      return <Portal>{DraggableContent}</Portal>;
    }

    return (
      <>
        {forceOpen === undefined && trigger && trigger(this.handleOpen)}
        <Modal
          open={open}
          onClose={this.handleClose}
          data-e2e-modal=""
          disableScrollLock={draggable}
          style={disableClose ? { zIndex: 9999 } : {}}
          role="dialog"
        >
          <Grid container spacing={0} alignItems="center" justifyContent="center" sx={classes.grid}>
            <Grid item xs={xs} sm={sm} md={md} lg={lg} marginLeft={2} marginRight={2}>
              <ClickAwayListener onClickAway={this.handleClickAway}>{DraggableContent}</ClickAwayListener>
            </Grid>
          </Grid>
        </Modal>
      </>
    );
  }
}

const mapStateToProps = (state: ApplicationState): PropsFromState => ({
  closeModal: state.ui.closeModal,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  dispatchResetCloseModalAction: () => dispatch(resetCloseModalAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SimpleModal);
